import React, { useId } from 'react';
import './Logo.css';

interface LogoProps {
    /** Drops the descriptor line under the wordmark. */
    compact?: boolean;
    /** Lockup scale. */
    size?: 'sm' | 'md' | 'lg';
    /** Overrides the descriptor line under the wordmark. */
    sub?: string;
}

/**
 * Kifayti company lockup — the parent brand.
 *
 * The company is mostly a software business (five SaaS platforms, IT services)
 * that also runs a retail network, so the parent mark is a monogram rather
 * than a shopping bag: a bag would speak only for the stores. The bag belongs
 * to the retail division — see MartLogo.tsx.
 *
 * The mark reads as a platform tile holding a "K" whose arm has lifted away
 * from the stem: the stem is the established business the company was built
 * on, the detached chevron the technology moving out ahead of it. The tile's
 * gradient runs from the product-suite indigo through violet into the retail
 * orange, so both halves of the company are present in one mark.
 */
const Logo: React.FC<LogoProps> = ({ compact = false, size = 'md', sub }) => {
    // The mark renders in the header and the footer at once, so the gradient
    // needs an id unique to each instance.
    const uid = useId().replace(/:/g, '');

    return (
        <span className={`logo logo--${size} ${compact ? 'logo--compact' : ''}`}>
            <span className="logo__mark" aria-hidden="true">
                <svg viewBox="0 0 40 40" role="presentation" focusable="false">
                    <defs>
                        <linearGradient id={`kg-${uid}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="50%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#ff5a1f" />
                        </linearGradient>
                    </defs>

                    <rect width="40" height="40" rx="11" fill={`url(#kg-${uid})`} />

                    {/* Stem */}
                    <rect x="10.5" y="10.5" width="4.4" height="19" rx="2.2" fill="#fff" />

                    {/* Chevron, held just clear of the stem so the counter stays
                        open at favicon sizes and the arm reads as detached. */}
                    <path
                        className="logo__chevron"
                        d="M27.4 11.4 17.9 20 27.6 28.6"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="4.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>

            <span className="logo__text">
                <span className="logo__word">Kifayti</span>
                {!compact && <span className="logo__sub">{sub ?? 'Retail Pvt. Ltd.'}</span>}
            </span>
        </span>
    );
};

export default Logo;
